/**
 * Client-side auth. Talks to server/index.ts's /api/auth/* endpoints,
 * which are backed by real Supabase accounts when the server has one
 * configured, and fall back to a dev-only name-based login otherwise —
 * see server/authAdapter.ts and server/supabaseClients.ts for the full
 * picture. This file is the client half: it never decides who's allowed
 * in itself (that's the server's httpOnly session cookie, which this
 * code can't even read), it only asks the server and renders accordingly.
 *
 * This has to work in three situations:
 *  1. No backend at all — a static-only deployment, or the plain
 *     `vite preview` server the e2e tests run against. Every /api/* call
 *     fails, so sign-in falls back to a purely local, unauthenticated-
 *     by-the-server "dev" session — exactly what the devAuthAdapter this
 *     file replaces used to do unconditionally.
 *  2. A backend is up but Supabase isn't configured (local dev without a
 *     Supabase project) — still a name-only login, but confirmed against
 *     the real server, so things like /api/sync/progress see a real
 *     session rather than one the server has never heard of.
 *  3. A backend + Supabase are both configured (production) — real
 *     email+password accounts, verified server-side.
 *
 * A small localStorage cache of {userId, displayName, role} — NOT a
 * credential; the real session is the httpOnly cookie, which this can't
 * read — exists purely to bridge an offline reload: if the device has no
 * network right now, the app still renders the last-known signed-in
 * state instead of bouncing to the sign-in screen just because a
 * *connectivity* check failed (spec requirement: a previously-imported
 * job must still open and browse with the device in airplane mode). It's
 * revalidated against the server whenever reachable and cleared outright
 * on an explicit 401 (a real sign-out) or explicit sign-out.
 */
import { api, ApiError } from '../api/client';
import type { UserRole } from '../db/schema';

export interface Session {
  userId: string;
  displayName: string;
  role: UserRole;
}

export type AuthMode = 'supabase' | 'dev' | 'unavailable';

const CACHE_KEY = 'qr-cards-session-cache';
const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // matches the server's session cookie maxAge

interface CachedSession extends Session {
  cachedAt: number;
}

export function getCachedSession(): Session | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedSession;
    if (Date.now() - cached.cachedAt > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return { userId: cached.userId, displayName: cached.displayName, role: cached.role };
  } catch {
    return null;
  }
}

function writeCache(session: Session): void {
  try {
    const cached: CachedSession = { ...session, cachedAt: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    // best-effort — private browsing, storage full, etc.
  }
}

function clearCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

/** Which sign-in UI to show. Any failure to reach the server at all
 * resolves to 'unavailable' — the sign-in screen renders it the same as
 * 'dev' (a name-only form); the two differ only in whether the resulting
 * session is confirmed by the server or assumed locally. */
export async function fetchAuthMode(): Promise<AuthMode> {
  try {
    const { mode } = await api.get<{ mode: 'supabase' | 'dev' }>('/api/auth/config', 4000);
    return mode;
  } catch {
    return 'unavailable';
  }
}

export interface SessionCheckResult {
  session: Session | null;
  /** False if the server could not be reached at all (offline, timeout,
   * no backend at this path) — as opposed to a definitive "not signed
   * in" answer. Callers use this to decide whether a null session means
   * "really signed out" or "couldn't check, so don't change anything". */
  reachable: boolean;
}

/** Asks the server who (if anyone) the current httpOnly cookie belongs
 * to. */
export async function checkCurrentSession(timeoutMs = 6000): Promise<SessionCheckResult> {
  try {
    const session = await api.get<Session>('/api/auth/me', timeoutMs);
    writeCache(session);
    return { session, reachable: true };
  } catch (err) {
    const status = err instanceof ApiError ? err.status : 0;
    if (status === 401) clearCache(); // a real, authoritative sign-out
    return { session: null, reachable: status !== 0 };
  }
}

export async function signInWithPassword(email: string, password: string): Promise<Session> {
  await api.post('/api/auth/login', { email, password });
  const { session, reachable } = await checkCurrentSession();
  if (!session || !reachable) {
    throw new Error('Signed in, but could not load your account — try again.');
  }
  return session;
}

/** Dev-only / no-backend sign-in: a display name, nothing else.
 * `verifyWithServer` should be true only when fetchAuthMode() already
 * confirmed a real (non-Supabase) backend is reachable — otherwise this
 * skips the network entirely and returns a purely local session, which
 * is what keeps a static-only deployment (and the e2e tests, which run
 * against a plain static preview server) usable. */
export async function signInDevOnly(displayName: string, opts: { verifyWithServer: boolean }): Promise<Session> {
  const trimmed = displayName.trim();
  if (!trimmed) throw new Error('Enter a name to continue.');

  if (!opts.verifyWithServer) {
    const localSession: Session = {
      userId: `dev-${trimmed.toLowerCase().replace(/\s+/g, '-')}`,
      displayName: trimmed,
      role: 'photographer'
    };
    writeCache(localSession);
    return localSession;
  }

  await api.post('/api/auth/login', { name: trimmed });
  const { session, reachable } = await checkCurrentSession();
  if (!session || !reachable) {
    throw new Error('Signed in, but could not load your account — try again.');
  }
  return session;
}

export async function signOut(): Promise<void> {
  clearCache();
  try {
    await api.post('/api/auth/logout', undefined, 4000);
  } catch {
    // Best-effort — if the server's unreachable there's nothing to
    // revoke server-side anyway, and the local cache is already gone.
  }
}
